import { useI18n } from 'vue-i18n'
import { ref, type Ref } from 'vue'
import { useBetslipStore } from '@/stores/betslip'
import { useGameStore } from '@/stores/game'
import { useTransactionsStore } from '@/stores/transactions'
import { useModalStore } from '@/stores/modal'
import { useToast } from '@/composables/useToast'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { resolveRebetDraws, type UpcomingDrawOption } from '@/utils/rebetDraws'
import type { BetslipItem } from '@/stores/betslip'
import { mergeRebetDraws, rebetLineKey } from '@/utils/rebetLines'

// Both entry points share a lock, scoped to their betslip store.
const rebetStates = new WeakMap<object, Ref<boolean>>()

function toBetslipDraws(draws: UpcomingDrawOption[]): BetslipItem['draws'] {
  return draws.map((draw) => ({
    draw_date: draw.draw_date,
    cutoff: draw.cutoff ?? draw.draw_date,
    event_id: draw.event_id,
  }))
}

type WagerBet = {
  market_name?: string
  market_text?: string
  betType?: string
  odds?: string
  market_id?: number
  outcome_id?: number
  game_market_id?: number
}

type RebetMarketOutcome = {
  id: number
  odds: string
  text?: string
  specifier?: string | null
  select_numbers?: boolean
}

function formatOutcomeLabel(outcome: RebetMarketOutcome): string {
  let text = (outcome.text ?? '').trim()
  if (text.includes('{value}') && outcome.specifier) {
    text = text.replace('{value}', outcome.specifier)
  }
  return text
}

/** Wager API often sends betType "Single"; use market_text / outcome label like a fresh bet. */
function resolveRebetBetType(bet: WagerBet, outcome: RebetMarketOutcome): string {
  const marketText = bet.market_text?.trim()
  if (marketText) return marketText
  if (bet.betType && bet.betType !== 'Single') return bet.betType
  const fromOutcome = formatOutcomeLabel(outcome)
  if (fromOutcome) return fromOutcome
  return bet.betType || 'Single'
}

function findRebetMarketMatch(
  bet: WagerBet,
  marketGroups: Array<{
    id: number
    markets: Array<{
      id: number
      name: string
      outcomes?: RebetMarketOutcome[]
    }>
  }>
) {
  const betMarketId = bet.market_id != null ? Number(bet.market_id) : null
  const betOutcomeId = bet.outcome_id != null ? Number(bet.outcome_id) : null

  for (const marketGroup of marketGroups) {
    for (const market of marketGroup.markets) {
      if (betMarketId != null && Number(market.id) !== betMarketId) {
        continue
      }
      if (betMarketId == null && market.name !== bet.market_name) {
        continue
      }
      if (!market.outcomes?.length) {
        continue
      }

      for (const outcome of market.outcomes) {
        if (betOutcomeId != null) {
          if (Number(outcome.id) === betOutcomeId) {
            return { matchedMarket: market, matchedOutcome: outcome, matchedMarketGroup: marketGroup }
          }
          continue
        }
        if (Math.abs(parseFloat(outcome.odds) - parseFloat(String(bet.odds))) < 0.01) {
          return { matchedMarket: market, matchedOutcome: outcome, matchedMarketGroup: marketGroup }
        }
      }
    }
  }
  return null
}

export type ApplyRebetOptions = {
  /** Called after rebet lines were added (e.g. close details modal). */
  onComplete?: () => void
  /** Open the betslip drawer/modal (wagers flow). Default true. */
  openBetslip?: boolean
}

export function useRebetFromTransaction() {
  const { t } = useI18n()
  const { showToast } = useToast()
  const transactionsStore = useTransactionsStore()
  const gameStore = useGameStore()
  const betslipStore = useBetslipStore()
  const modalStore = useModalStore()
  let isRebetting = rebetStates.get(betslipStore)
  if (!isRebetting) {
    isRebetting = ref(false)
    rebetStates.set(betslipStore, isRebetting)
  }
  const rebetting = isRebetting

  const applyRebetFromCurrentTransaction = async (options: ApplyRebetOptions = {}) => {
    if (rebetting.value) return
    rebetting.value = true
    const { onComplete, openBetslip = true } = options

    try {
      const transaction = transactionsStore.currentTransaction
      if (!transaction?.games) {
        showToast({
          title: t('betslip.rebetUnavailableTitle'),
          message: t('betslip.noTransactionData'),
          type: 'error',
          duration: 6000,
          centered: true,
        })
        return
      }

      const preparedLines = new Map<string, BetslipItem>()
      let skippedUnavailableDraws = 0

      for (const [gameKey, games] of Object.entries(transaction.games)) {
        await gameStore.fetchGameDetails(gameKey)
        const upcomingDraws = gameStore.getValidDraws
        const marketGroups = gameStore.getMarketGroups
        for (const game of games as any[]) {
          const selectedDraws = resolveRebetDraws(game, upcomingDraws)

          if (selectedDraws.length === 0) {
            skippedUnavailableDraws += game.bets?.length ?? 0
            console.warn(
              'Original draw no longer available for rebet:',
              gameKey,
              game.draw_id,
              game.draw_date
            )
            continue
          }

          for (const bet of game.bets) {
            const match = findRebetMarketMatch(bet, marketGroups)
            const matchedMarket = match?.matchedMarket ?? null
            const matchedOutcome = match?.matchedOutcome ?? null
            const matchedMarketGroup = match?.matchedMarketGroup ?? null

            if (!matchedMarket || !matchedOutcome || !matchedMarketGroup) {
              console.warn('Could not find matching market/outcome for bet:', bet)
              continue
            }

            const rebetBetType = resolveRebetBetType(bet, matchedOutcome)

            const betslipItem: BetslipItem = {
              market_id: matchedMarket.id,
              market_group_id: matchedMarketGroup.id,
              game_name: gameStore.getGameDetail?.name,
              game_id: gameStore.getGameDetail?.id || 0,
              market_name: bet.market_name || '',
              market_description: bet.market_group_description || '',
              numbers: bet.bet_numbers || [],
              stake: Number(bet.bet_cost),
              betType: rebetBetType,
              maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
              odds: Number(bet.odds),
              draws: toBetslipDraws(selectedDraws),
              allAvailableDraws: toBetslipDraws(upcomingDraws),
              drawCount: selectedDraws.length,
              picks: (bet as any).picks || 0,
              numBets: 1,
              outcome_id: matchedOutcome.id.toString(),
              label: rebetBetType,
              specifier: matchedOutcome.specifier || null,
              select_numbers: matchedOutcome.select_numbers || false,
              currency_symbol:
                gameStore.getBetSettings?.currency_symbol ||
                (transaction as any).currency_symbol ||
                '€',
            }

            const key = rebetLineKey(betslipItem)
            const existing = preparedLines.get(key)
            if (existing) {
              existing.draws = mergeRebetDraws(existing.draws, betslipItem.draws)
              existing.drawCount = existing.draws.length
            } else {
              preparedLines.set(key, betslipItem)
            }
          }
        }
      }

      if (preparedLines.size === 0) {
        showToast({
          title: t('betslip.rebetUnavailableTitle'),
          message:
            skippedUnavailableDraws > 0
              ? t('betslip.rebetDrawUnavailable')
              : t('betslip.rebetNothingAdded'),
          type: 'error',
          duration: 6000,
          centered: true,
        })
        return
      }

      for (const line of preparedLines.values()) {
        const { changed, item } = betslipStore.addRebet(line)
        if (changed) {
          iframeCommunication.sendMessage('bets-added', { bets: [item] })
        }
      }

      onComplete?.()
      if (openBetslip) {
        modalStore.openBetslip()
      }
    } catch (error) {
      console.error('Rebet error:', error)
      showToast({
        title: t('betslip.rebetUnavailableTitle'),
        message: t('betslip.failedToAddBets'),
        type: 'error',
        duration: 6000,
        centered: true,
      })
    } finally {
      rebetting.value = false
    }
  }

  return { applyRebetFromCurrentTransaction, isRebetting: rebetting }
}
